import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { ISimpleUser } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { addUserToDesk } from "src/redux/reducers/DispatchDeskReducer";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IAddUserDialogProps {
  open: boolean;
  onClose: () => void;
  deskId: string;
  existingUserIds: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AddUserDialog({ open, onClose, deskId, existingUserIds }: IAddUserDialogProps): JSX.Element {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state: IRootState) => state.authentication.attributes?._id);
  const isCurrentUserOnDesk = existingUserIds.includes(currentUserId);

  const [users, setUsers] = useState<ISimpleUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ISimpleUser | null>(null);

  // Fetch users when dialog opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      setSelectedUser(null);
      AmplifyAPIWrapper.GetPaginate<ISimpleUser>("/users?simpleUser=true")
        .then((response) => {
          setUsers(response ?? []);
        })
        .finally(() => setLoading(false));
    }
  }, [open]);

  // Filter out users already on the desk
  const availableUsers = useMemo(
    () => users.filter((u: ISimpleUser) => u.active && !existingUserIds.includes(u._id)),
    [users, existingUserIds]
  );

  const handleAdd = useCallback(() => {
    if (selectedUser) {
      dispatch(addUserToDesk({ deskId, userId: selectedUser._id }));
      onClose();
    }
  }, [dispatch, deskId, selectedUser, onClose]);

  const handleAddMe = useCallback(() => {
    if (currentUserId) {
      dispatch(addUserToDesk({ deskId, userId: currentUserId }));
      onClose();
    }
  }, [dispatch, deskId, currentUserId, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add User to Desk</DialogTitle>
      <DialogContent>
        {/* Add Me shortcut */}
        {!isCurrentUserOnDesk && (
          <Box sx={{ mb: 2 }}>
            <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={handleAddMe} fullWidth>
              Add Me
            </Button>
          </Box>
        )}

        {/* Search by name */}
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Or search for a user:
        </Typography>
        <Autocomplete<ISimpleUser>
          options={availableUsers}
          loading={loading}
          getOptionLabel={(option: ISimpleUser) => {
            const displayName = option.preferredName
              ? `${option.preferredName} (${option.firstName} ${option.lastName})`
              : `${option.firstName} ${option.lastName}`;
            return option.employeeId ? `${displayName} — ${option.employeeId}` : displayName;
          }}
          value={selectedUser}
          onChange={(_event: unknown, value: ISimpleUser | null) => setSelectedUser(value)}
          renderInput={(params: any) => (
            <TextField {...params} label="Search by name" variant="outlined" size="small" autoFocus />
          )}
          isOptionEqualToValue={(option: ISimpleUser, value: ISimpleUser) => option._id === value._id}
          noOptionsText="No matching users"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd} disabled={!selectedUser}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
